// OPTIONS ANALYSIS · Option Chain — read-only view of the SAME feed records the scanner uses.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Layers } from "lucide-react";
import { age, EconomicsNote, MarketContext, PauseButton, px, qty, ScannerStatusBar, StatusPill, UnderlyingPicker, usePoll } from "@/components/options/OptionsCommon";
import { ContractDepthDrawer } from "@/components/options/DepthDrawer";
import { impliedVol, ivPrice, yearsToExpiry } from "@/lib/iv";

function Side({ s, call, strike, F, T }) {
  const td = "px-1.5 py-1 text-right mono whitespace-nowrap";
  if (!s) return <td colSpan={10} className="text-center text-[var(--term-muted)]">—</td>;
  const iv = impliedVol(call, ivPrice(s), F, strike, T);
  const cells = [
    <td key="oi" className={td}>{qty(s.oi)}</td>,
    <td key="vol" className={td}>{qty(s.volume)}</td>,
    <td key="bq" className={td}>{qty(s.bid_qty)}</td>,
    <td key="b" className={`${td} text-[#2563EB]`}>{px(s.bid)}</td>,
    <td key="l" className={`${td} font-bold`}>{px(s.ltp)}</td>,
    <td key="iv" className={`${td} text-[var(--term-secondary)]`} data-testid="chain-iv"
      title="Implied volatility, Black-76 on the underlying shown, r = 0, from the bid/ask mid (else the LTP). Display only">{iv == null ? "—" : `${(iv * 100).toFixed(1)}%`}</td>,
    <td key="a" className={`${td} text-[#FF3B30]`}>{px(s.ask)}</td>,
    <td key="aq" className={td}>{qty(s.ask_qty)}</td>,
    <td key="t" className={`${td} text-[var(--term-muted)]`} title={`last trade ${s.last_trade_time || "—"}`}>{age(s.tick_age_s)} / {age(s.trade_age_s)}</td>,
    <td key="s" className="px-1.5 py-1 text-center text-[10px]">{s.subscribed ? (s.itm ? <b className="text-[#00C805]">ITM</b> : "sub") : <span className="text-[var(--term-muted)]" title="Outside the streamed band: no live data for this contract">not streamed</span>}</td>,
  ];
  return call ? cells : cells.reverse();
}

export default function OptionChain() {
  const status = usePoll("/status", { ms: 4000 });
  const unds = useMemo(() => status.data?.runtime?.scanner?.underlyings || [], [status.data]);
  const [und, setUnd] = useState("");
  const [paused, setPaused] = useState(false);
  const [depth, setDepth] = useState(null);
  useEffect(() => { if (!und && unds.length) setUnd(unds[0].underlying); }, [unds, und]);
  const [bandOnly, setBandOnly] = useState(true);
  const chain = usePoll(und ? "/chain" : null, { ms: 2000, paused, params: { underlying: und } });
  const d = chain.data;
  const strikes = (d?.strikes || []).filter((r) => !bandOnly || r.ce?.subscribed || r.pe?.subscribed);
  const spotRef = useRef(null);
  const scrolledFor = useRef(null);
  useEffect(() => {                       // bring the underlying price into view once per underlying
    if (spotRef.current && scrolledFor.current !== `${und}|${bandOnly}`) {
      spotRef.current.scrollIntoView({ block: "center" });
      scrolledFor.current = `${und}|${bandOnly}`;
    }
  });
  const head = ["OI", "Volume", "Bid Qty", "Bid", "LTP", "IV", "Offer", "Offer Qty", "Tick/Trade age", "Band"];
  const T = yearsToExpiry(d?.expiry, d?.market || d?.underlying_symbol);
  // ATM = the listed strike nearest the underlying
  const atm = d?.spot != null && strikes.length
    ? strikes.reduce((b, r) => (Math.abs(r.strike - d.spot) < Math.abs(b.strike - d.spot) ? r : b)).strike : null;
  // put/call OI over the strikes that carry OI: partial when the band streams only part of the chain
  const oi = (side) => (d?.strikes || []).reduce((a, r) => a + (Number(r[side]?.oi) || 0), 0);
  const pcr = oi("ce") > 0 ? oi("pe") / oi("ce") : null;
  return (
    <div className="p-4 space-y-3" data-testid="options-chain-page">
      <h1 className="text-lg font-bold flex items-center gap-2"><Layers className="h-5 w-5" /> Option Chain</h1>
      <ScannerStatusBar status={status.data} onChanged={status.reload} />
      <MarketContext market={d?.market || status.data?.settings?.market} universeLabel={status.data?.runtime?.scanner?.universe_label}
        feedState={status.data?.runtime?.feed_state} />
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <UnderlyingPicker underlyings={unds} value={und} onChange={setUnd} testId="options-chain-underlying" />
        <PauseButton paused={paused} setPaused={setPaused} />
        <label className="flex items-center gap-1"><input type="checkbox" checked={bandOnly} onChange={(e) => setBandOnly(e.target.checked)} data-testid="options-chain-band-only" /> subscribed band only</label>
        {d && <span className="mono text-[var(--term-muted)]">{d.underlying_symbol} <b className="text-[var(--term-primary)]">{px(d.spot)}</b> ({age(d.spot_age_s)}) · expiry {d.expiry}{T != null ? ` (${Math.max(0, Math.round(T * 365))} days)` : ""} · {d.strikes?.length} strikes{atm != null ? ` · ATM ${atm}` : ""} · <StatusPill status={d.status} /></span>}
        {pcr != null && <span className="mono text-[var(--term-muted)]" data-testid="options-chain-pcr"
          title="Total PE open interest ÷ total CE open interest over the strikes that carry OI. Partial: only the streamed band has OI">PCR (OI, streamed strikes) <b className="text-[var(--term-primary)]">{pcr.toFixed(2)}</b></span>}
        {chain.error && <span className="text-[#FF3B30]">{chain.error}</span>}
        {d?.economics && <EconomicsNote e={d.economics} />}
      </div>
      <div className="term-panel rounded-sm overflow-auto" style={{ maxHeight: "calc(100vh - 260px)" }}>
        <table className="w-full text-xs border-collapse" data-testid="options-chain-table">
          <thead className="sticky top-0 z-10 bg-[var(--term-s3)] text-[var(--term-secondary)]">
            <tr><th colSpan={10} className="py-1 text-center">CALLS (CE)</th><th /><th colSpan={10} className="py-1 text-center">PUTS (PE)</th></tr>
            <tr>
              {head.map((h) => <th key={`c${h}`} className="px-1.5 py-1 text-right whitespace-nowrap">{h}</th>)}
              <th className="px-2 py-1 text-center">Strike</th>
              {[...head].reverse().map((h) => <th key={`p${h}`} className="px-1.5 py-1 text-right whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {strikes.map((row, i, all) => {
              const spotLine = d.spot != null && i > 0 && all[i - 1].strike < d.spot && row.strike >= d.spot;
              return (
                <React.Fragment key={row.strike}>
                  {spotLine && <tr ref={spotRef}><td colSpan={21} className="text-center text-[10px] font-bold bg-[#FFCC00]/20">underlying {px(d.spot)}</td></tr>}
                  <tr className="border-t border-[var(--term-border)] cursor-pointer hover:bg-[var(--term-hover)]" data-testid={`options-chain-row-${row.strike}`}
                    title="Click for the five-level market depth of this strike's CE and PE"
                    onClick={() => setDepth({
                      heading: `${d.underlying} ${row.strike} · ${d.expiry}`,
                      legs: [row.ce && { token: row.ce.instrument_token, title: "CALL (CE)" },
                             row.pe && { token: row.pe.instrument_token, title: "PUT (PE)" }].filter(Boolean),
                    })}>
                    <Side s={row.ce} call strike={row.strike} F={d.spot} T={T} />
                    <td className={`px-2 py-1 text-center font-bold mono ${row.strike === atm ? "bg-[#FFCC00]/30" : "bg-[var(--term-s3)]"}`}
                      data-testid={row.strike === atm ? "options-chain-atm" : undefined}>{row.strike}{row.strike === atm && <div className="text-[9px] text-[#b38f00]">ATM</div>}</td>
                    <Side s={row.pe} strike={row.strike} F={d.spot} T={T} />
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-[var(--term-muted)]">
        Only contracts inside the subscription band (ITM window + buffer) are streamed; "not streamed" strikes carry no live data,
        so the PCR covers the streamed strikes only. "ITM" marks the contracts the scanner compares. IV is Black-76 on the underlying
        shown (r = 0), from the bid/ask mid or else the LTP — an indication, not a model the scanner uses. Click a strike for its depth.
      </div>
      {depth && <ContractDepthDrawer legs={depth.legs} heading={depth.heading} onClose={() => setDepth(null)} />}
    </div>
  );
}
